// -----------------------------------------------------------------------------
// EstatePlanning.jsx — canonical page for /texas-estate-planning-attorney/
//
// Statewide Texas Estate Planning authority hub. Coordinated documents for
// families, executives, professionals, and business owners. Editorial pattern
// matches the four existing authority pages (APA, Succession, Trademark,
// Physician). Data-in-file for velocity — the priority-FAQ and section content
// is small enough that a dedicated content module would be over-engineering.
// -----------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import AttorneyReviewNote from "@/components/AttorneyReviewNote";
import { breadcrumbSchema, faqPageSchema, legalServiceSchema, attorneySchema } from "@/lib/schemas";
import { trackAuthorityPageView, trackPriorityFaqOpen } from "@/lib/analytics";
import { ChevronDown, Phone, Star } from "lucide-react";

const PHONE = "(972) 712-1515";
const PHONE_TEL = "9727121515";
const CANONICAL = "/texas-estate-planning-attorney/";

const IN_PAGE_NAV = [
  ["Documents", "documents"],
  ["Business Owners", "business-owners"],
  ["When to Review", "review"],
  ["FAQs", "faqs"],
  ["Related", "related"],
  ["Contact", "contact"],
];

// Coordinated documents in a Texas estate plan — the working list.
const COORDINATED_DOCUMENTS = [
  ["Last Will and Testament", "Directs the disposition of assets that pass through Texas probate at death. Names an executor and, when applicable, a guardian for minor children."],
  ["Revocable Living Trust (when appropriate)", "Holds assets in the name of a trustee during life, incapacity, and death. For properly funded trust assets, can avoid probate. Not every plan needs one — selection depends on the family and the assets."],
  ["Pour-Over Will (with a trust)", "Directs any assets not already titled to the trust into the trust at death, so the plan captures late-acquired or overlooked property."],
  ["Durable Financial Power of Attorney", "Grants a named agent authority over financial affairs during incapacity, avoiding a court-appointed guardianship for the estate."],
  ["Medical Power of Attorney", "Grants a named agent authority to make medical decisions when the person cannot make or communicate them."],
  ["HIPAA Authorization", "Permits designated persons to receive protected health information from providers — a coordinated document with the medical power of attorney."],
  ["Directive to Physicians (Living Will)", "Records the person's wishes regarding life-sustaining treatment in end-of-life circumstances defined by statute."],
  ["Declaration of Guardian", "Names the persons the individual would prefer as guardian for the estate and person in the event of later incapacity — and, separately, guardian for any minor children."],
  ["Beneficiary-Designation Review", "The coordinated review of retirement accounts, insurance, annuities, transfer-on-death registrations, and other non-probate designations so they match the plan."],
];

// Focal points for business-owner estate planning.
const BUSINESS_OWNER_POINTS = [
  ["Company-agreement coordination", "The operating agreement, shareholder agreement, or partnership agreement is reviewed for transfer restrictions, consent requirements, and any conflict with the trust or estate plan."],
  ["Buy-sell alignment", "The buy-sell agreement is coordinated with the estate plan so the transaction on death or disability is documented and funded — not surprised."],
  ["Successor decision-makers", "The plan names successor managers, trustees, or officers with the authority to keep the business operating during incapacity or between death and administration."],
  ["Family fairness", "Provisions for spouses, children active in the business, and children who are not — through non-voting interests, income beneficiary status, or coordinated life-insurance funding."],
  ["Professional-practice discipline", "Physicians, dentists, veterinarians, lawyers, and other licensed professionals face licensing rules that restrict who may own the practice; the estate plan is drafted around those rules, not against them."],
  ["Liquidity and tax coordination", "How the estate pays taxes, debts, and expenses without forcing a distressed sale of the business — including insurance, seller-note treatment, and the timing of transfers."],
];

// Triggers to review an estate plan.
const REVIEW_TRIGGERS = [
  ["Marriage, divorce, or serious change in a marriage", "The plan built for one household may not fit the next. Both prenuptial coordination and post-divorce review are common triggers."],
  ["Birth, adoption, or death of a beneficiary", "The class of beneficiaries and the shares they receive frequently need to be revisited."],
  ["Serious illness or diagnosis in the family", "Incapacity provisions, medical directives, and successor fiduciaries typically require immediate attention."],
  ["Significant change in wealth", "A sale, inheritance, investment event, or material appreciation may change the plan's tax posture and the appropriate structure."],
  ["Move to or from Texas", "State law drives will formalities, community-property treatment, probate procedure, and specific document requirements."],
  ["Sale, formation, or restructuring of a business", "The estate plan must be re-coordinated with the new company agreement and the new succession plan."],
  ["Death of a named fiduciary", "The executor, trustee, or agent named in the plan is no longer available; successors must be reviewed and, if needed, replaced."],
  ["Material change in tax, trust, or fiduciary law", "Legal changes that would meaningfully affect the plan on paper are grounds to review the plan in fact."],
];

// The eight curated priority FAQs (attorney-drafted, calm, no guarantees).
const PRIORITY_FAQ = [
  ["What documents belong in a Texas estate plan?",
   "A coordinated Texas estate plan typically includes (i) a will (and, when a revocable trust is used, a pour-over will), (ii) a revocable living trust where appropriate, (iii) a durable financial power of attorney, (iv) a medical power of attorney, (v) a HIPAA authorization for release of protected health information, (vi) a directive to physicians (living will), (vii) a declaration of guardian in the event of later incapacity, (viii) a declaration of guardian for minor children, and (ix) a coordinated review of beneficiary designations and account titling. Business owners typically add a coordinated succession plan, buy-sell coordination, and updated company agreements. The specific documents depend on the family, assets, business, and objectives."],
  ["What is the difference between a will and revocable living trust?",
   "A will directs the disposition of assets that pass through the Texas probate court after death; it takes effect only at death and does not address incapacity or non-probate assets. A revocable living trust is created and funded during life, holds assets in the name of a trustee, and can operate during incapacity, at death, or both. For properly funded trust assets, it can avoid probate. Many Texans have a coordinated plan that uses both — a revocable trust plus a pour-over will — while others use a will-based plan without a trust. Selection depends on the family, assets, and objectives, not on preferring one form over the other."],
  ["Who should serve as executor or trustee?",
   "The executor of a will and the trustee of a trust should be persons or institutions with the judgment, discipline, and impartiality to carry out fiduciary duties over time. Common choices include the surviving spouse, an adult child, a professional co-fiduciary, or a corporate trustee for larger, longer-duration, or more complex matters. Successors and back-ups are essential. Family dynamics, health, geography, and asset complexity all matter — the person best suited to be a loving parent is not always the person best suited to administer an estate or a trust."],
  ["Do spouses need separate wills?",
   "Generally, yes. Each spouse owns a separate estate under Texas law that requires its own disposition on death. Joint wills — a single document intended to serve as the will of both spouses — are rarely appropriate and often create problems. A coordinated plan typically consists of two separate but consistent wills, plus separate powers of attorney, medical directives, and (where used) trusts."],
  ["How do I name guardians for minor children?",
   "In Texas, a parent may nominate a guardian for a minor child in a written declaration and in the will. The court makes the final appointment, but a properly executed nomination is given substantial weight. The plan should identify primary and successor guardians, address financial responsibility separately from personal responsibility if appropriate, and consider access to any inheritance the child may receive. A trust for the child's benefit can separate the person who cares for the child from the person who manages the money."],
  ["What happens if I die without a will in Texas?",
   "Under Texas intestacy law, the deceased person's property passes to a defined class of heirs — typically the surviving spouse, children, and, in their absence, more distant relatives — in shares set by statute. The court supervises administration, appointment of a personal representative, and (if there are minor children) any guardianship. Intestate outcomes are frequently different from what the family expected, and can be more expensive and more contested than the estate the deceased person would have designed. A basic will is nearly always better than intestacy."],
  ["How often should I update my estate plan?",
   "A coordinated review is advisable every 3 to 5 years, and immediately after any of the following: a marriage, divorce, or serious change in a marriage; the birth, adoption, or death of a child or other beneficiary; a serious illness or diagnosis in the family; a significant change in wealth; a move to or from Texas; the sale, formation, or restructuring of a business; the death of a named fiduciary; or a material change in tax, trust, or fiduciary law. A plan that fit five years ago may no longer fit today."],
  ["How does estate planning affect a business owner?",
   "Substantially. For an owner of a Texas business, the estate plan must be coordinated with the company agreement, buy-sell agreement, and succession plan — otherwise, the plan on paper and the plan in fact may not describe the same transaction. Coordinated estate planning for a business owner typically addresses who can manage the company during incapacity, who receives the ownership interest at death, whether the company agreement permits the transfer, how successor leaders are chosen, and how the family (including family members not in the business) is protected. This is why business owners are one of the firm's core estate-planning constituencies."],
];

// Contextual cross-links to related authority hubs.
const CONTEXTUAL_LINKS = [
  { label: "Trusts", desc: "The Texas trust hub — revocable, irrevocable, special-needs, asset-protection, and charitable planning.", href: "/trusts" },
  { label: "Business-Owner Trust Planning", desc: "Trust ownership of business interests coordinated with the company agreement and estate plan.", href: "/trusts/business-owner-trust-planning" },
  { label: "Business Succession", desc: "The longer-arc succession plan — retirement, family transfer, management buyout, and third-party sale.", href: "/texas-business-succession-planning-attorney/" },
  { label: "Probate", desc: "Administration of Texas estates through the probate court when assets pass by will or intestacy.", href: "/texas-probate-attorney/" },
];

function FaqItem({ q, a, i, onOpen }) {
  const [open, setOpen] = useState(i === 0);
  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next && onOpen) onOpen();
      return next;
    });
  };
  return (
    <div className="hairline-top py-6" data-testid={`estate-faq-${i}`}>
      <button
        onClick={toggle}
        aria-expanded={open}
        className="w-full flex justify-between items-start gap-6 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-brass"
      >
        <h3 className="font-display text-xl md:text-2xl text-navy leading-tight">{q}</h3>
        <ChevronDown
          size={22}
          className={`shrink-0 text-brass transition-transform mt-1 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div className={`${open ? "block" : "hidden"} mt-4 text-slate2 font-serif text-base leading-relaxed`}>
        {a}
      </div>
    </div>
  );
}

export default function EstatePlanningFlagship() {
  useEffect(() => {
    trackAuthorityPageView({
      page_slug: "texas-estate-planning-attorney",
      page_title: "Texas Estate Planning Attorney",
      priority_faq_count: PRIORITY_FAQ.length,
    });
  }, []);

  return (
    <div className="bg-ivory" data-testid="estate-planning-page">
      <SEO
        title="Texas Estate Planning Attorney | Wills, Trusts & Powers of Attorney | Lone Star Counsel"
        description="Texas estate planning for families and business owners: wills, trusts, powers of attorney, medical directives, guardianship planning, and coordinated legacy protection."
        keywords="Texas Estate Planning Attorney, Texas will attorney, revocable living trust Texas, power of attorney Texas, medical directive Texas, guardianship Texas, HIPAA authorization Texas, family estate planning Texas, business owner estate planning Texas"
        path={CANONICAL}
        schema={[
          legalServiceSchema({
            name: "Texas Estate Planning Attorney",
            description: "Coordinated Texas estate plans for families, executives, professionals, and business owners — wills, revocable trusts, powers of attorney, medical directives, guardianship planning, and business-succession coordination.",
            url: `https://lonestarcounsel.com${CANONICAL}`,
          }),
          attorneySchema(),
          faqPageSchema(PRIORITY_FAQ.map(([q, a]) => ({ q, a }))),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Texas Estate Planning Attorney", path: CANONICAL },
          ]),
        ]}
      />

      {/* HERO */}
      <section className="bg-ivory" data-testid="estate-hero">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-8">
            <div className="kicker">Lone Star &#9733; Counsel</div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-navy">
              Texas Estate Planning<br />
              <span className="italic text-oxblood">Attorney.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg md:text-xl text-slate2 font-serif leading-relaxed" data-testid="estate-direct-answer">
              Lone Star Counsel prepares coordinated Texas estate plans for families, executives,
              professionals, and business owners. A complete plan can include a will, revocable
              living trust when appropriate, financial and medical powers of attorney, HIPAA
              authorization, directive to physicians, declaration of guardian, beneficiary-
              designation review, and business-succession coordination. The purpose is to protect
              the people and property you care about, reduce uncertainty during incapacity or
              death, and leave clear instructions rather than conflict.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <Link to="/intake" className="btn-primary" data-testid="estate-hero-cta-primary">
                Schedule an Estate Planning Conference
              </Link>
              <a
                href={`tel:${PHONE_TEL}`}
                className="text-[11px] uppercase tracking-[0.32em] text-navy hover:text-oxblood transition-colors font-medium border-b border-brass/50 hover:border-oxblood pb-1"
                data-testid="estate-hero-cta-phone"
              >
                <Phone size={11} className="inline mr-2" />
                or call {PHONE}
              </a>
            </div>
            <div className="mt-8 max-w-2xl">
              <AttorneyReviewNote reviewed="February 2026" compact />
            </div>
          </div>
          <aside className="lg:col-span-4">
            <nav aria-label="On this page" className="sticky top-24 border-l border-brass/40 pl-6" data-testid="estate-in-page-nav">
              <div className="kicker">On this page</div>
              <ul className="mt-4 space-y-2 text-sm font-serif">
                {IN_PAGE_NAV.map(([label, id]) => (
                  <li key={id}>
                    <a href={`#${id}`} className="text-navy/70 hover:text-oxblood transition-colors">
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>
        </div>
      </section>

      {/* DOCUMENTS — Coordinated plan grid */}
      <section id="documents" className="bg-linen hairline-top py-20 md:py-28" data-testid="estate-documents">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="kicker">Scope of a coordinated plan</div>
          <h2 className="mt-6 font-display text-navy text-3xl md:text-5xl leading-[1.05]">
            What a Coordinated Texas <span className="italic text-oxblood">Estate Plan May Include.</span>
          </h2>
          <p className="mt-6 max-w-3xl text-navy font-serif leading-relaxed">
            The working list below is a starting point, not a fixed menu. Every plan is scoped to
            the specific family, assets, and objectives — and coordinated with the client&apos;s CPA,
            financial advisor, insurance professional, and other advisors as appropriate. Not every
            plan needs every document; the right plan is the one that fits.
          </p>
          <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2" data-testid="estate-documents-list">
            {COORDINATED_DOCUMENTS.map(([title, desc], i) => (
              <li key={i} className="py-4 hairline-top" data-testid={`estate-doc-${i}`}>
                <div className="flex items-baseline gap-3">
                  <Star size={9} className="text-brass fill-brass" aria-hidden />
                  <h3 className="font-display text-lg text-navy">{title}</h3>
                </div>
                <p className="mt-2 pl-6 text-sm text-navy/75 leading-relaxed font-serif">{desc}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* BUSINESS OWNERS + PROFESSIONALS */}
      <section id="business-owners" className="bg-ivory py-20 md:py-28" data-testid="estate-business-owners">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="kicker">A core constituency</div>
              <h2 className="mt-6 font-display text-navy text-3xl md:text-5xl leading-[1.05]">
                Estate Planning for <span className="italic text-oxblood">Business Owners &amp; Professionals.</span>
              </h2>
              <p className="mt-6 text-navy font-serif leading-relaxed">
                For an owner of a Texas business, the estate plan is not a stand-alone document. It
                is the instrument that ties the will, the revocable trust, the company agreement,
                the buy-sell agreement, and the succession plan into a single, coordinated event.
                Getting them consistent is what turns a plan on paper into a plan in fact.
              </p>
              <p className="mt-4 text-navy/85 font-cormorant italic text-lg leading-snug border-l-2 border-brass pl-4">
                The estate plan describes the transition. The company agreement enforces it. The
                buy-sell agreement funds it. When they contradict each other, the transaction
                terms usually win at the worst possible moment.
              </p>
            </div>
            <div className="lg:col-span-7">
              <ul className="space-y-4" data-testid="estate-business-owner-list">
                {BUSINESS_OWNER_POINTS.map(([title, desc], i) => (
                  <li key={i} className="hairline-top pt-5" data-testid={`estate-biz-${i}`}>
                    <div className="flex items-baseline gap-3">
                      <span className="text-brass" aria-hidden>&#9733;</span>
                      <h3 className="font-display text-lg text-navy">{title}</h3>
                    </div>
                    <p className="mt-2 pl-6 text-sm text-navy/75 leading-relaxed font-serif">{desc}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHEN TO REVIEW */}
      <section id="review" className="bg-linen hairline-top py-20 md:py-28" data-testid="estate-review-triggers">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="kicker">Cadence of review</div>
          <h2 className="mt-6 font-display text-navy text-3xl md:text-5xl leading-[1.05]">
            When to Review <span className="italic text-oxblood">an Estate Plan.</span>
          </h2>
          <p className="mt-6 max-w-3xl text-navy font-serif leading-relaxed">
            A coordinated review every 3 to 5 years, and any time one of the following occurs.
            The plan that fit five years ago may no longer fit today &mdash; and a plan that no
            longer fits is the plan that is most likely to fail at the moment it is triggered.
          </p>
          <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2" data-testid="estate-review-list">
            {REVIEW_TRIGGERS.map(([title, desc], i) => (
              <li key={i} className="py-4 hairline-top" data-testid={`estate-trigger-${i}`}>
                <div className="flex items-baseline gap-3">
                  <span className="text-brass" aria-hidden>&#9733;</span>
                  <h3 className="font-display text-lg text-navy">{title}</h3>
                </div>
                <p className="mt-2 pl-6 text-sm text-navy/75 leading-relaxed font-serif">{desc}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQS */}
      <section id="faqs" className="bg-ivory py-20 md:py-28" data-testid="estate-faq">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="kicker">Frequently Asked Questions</div>
          <h2 className="mt-6 font-display text-3xl md:text-5xl text-navy leading-[1.02]">
            Answers <span className="italic text-oxblood">before the plan.</span>
          </h2>
          <p className="mt-4 max-w-3xl text-navy/75 font-serif leading-relaxed">
            The most common questions we receive from Texas families and business owners,
            answered directly. General information only &mdash; not legal advice on your specific
            matter.
          </p>
          <div className="mt-10" data-testid="estate-priority-faq">
            {PRIORITY_FAQ.map(([q, a], i) => (
              <FaqItem
                key={i}
                q={q}
                a={a}
                i={i}
                onOpen={() =>
                  trackPriorityFaqOpen({
                    page_slug: "texas-estate-planning-attorney",
                    faq_position: i + 1,
                    faq_question: q,
                  })
                }
              />
            ))}
          </div>
        </div>
      </section>

      {/* RELATED / CONTEXTUAL LINKS */}
      <section id="related" className="bg-linen hairline-top py-20 md:py-28" data-testid="estate-related">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="kicker">Related counsel</div>
          <h2 className="mt-6 font-display text-3xl md:text-5xl text-navy leading-[1.05]">
            Coordinate the plan{" "}
            <span className="italic text-oxblood">across the disciplines.</span>
          </h2>
          <p className="mt-6 max-w-3xl text-navy font-serif leading-relaxed">
            Estate planning is rarely a single document and rarely a single discipline. The
            authority hubs below cover the surrounding work that the estate plan must speak to.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="estate-related-list">
            {CONTEXTUAL_LINKS.map((c, i) => (
              <Link
                key={i}
                to={c.href}
                className="group hairline-top pt-5 block hover:bg-ivory/60 transition-colors -mx-3 px-3"
                data-testid={`estate-related-${i}`}
              >
                <div className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">
                  {c.label}
                </div>
                <h3 className="mt-3 font-display text-lg md:text-xl text-navy leading-snug group-hover:text-oxblood transition-colors">
                  {c.desc}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section id="contact" className="panel-navy grain grain-dark py-20 md:py-28 relative overflow-hidden" data-testid="estate-cta">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 text-center">
          <Star size={11} className="text-brass fill-brass mx-auto" aria-hidden />
          <div className="mt-8 kicker kicker-ivory">A message from the firm</div>
          <h2 className="mt-4 font-display text-ivory text-3xl md:text-5xl leading-[1.05] max-w-3xl mx-auto">
            Leave Instructions.{" "}
            <span className="italic font-cormorant text-brass">Not Conflict.</span>
          </h2>
          <p className="mt-8 max-w-2xl mx-auto text-ivory/85 font-serif text-lg leading-relaxed">
            A well-designed estate plan protects the people and property you care about, reduces
            uncertainty during incapacity or death, and gives your family clear, coordinated
            instructions rather than a set of open questions. It is not a form &mdash; it is the
            plan that speaks for you when you cannot.
          </p>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-6">
            <Link to="/intake" className="btn-brass" data-testid="estate-cta-primary">
              Schedule a Confidential Conference
            </Link>
            <a
              href={`tel:${PHONE_TEL}`}
              className="text-[11px] uppercase tracking-[0.32em] text-ivory/85 hover:text-brass transition-colors font-medium border-b border-brass/40 hover:border-brass pb-1"
              data-testid="estate-cta-phone"
            >
              <Phone size={11} className="inline mr-2" />
              or call {PHONE}
            </a>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-navy text-ivory/70 py-10 text-xs">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 font-serif leading-relaxed">
          <strong className="text-brass tracking-widest uppercase text-[10px]">Disclaimer.</strong>{" "}
          The information on this page is for general informational purposes only and is not
          legal, tax, accounting, or financial advice. Reading this page or contacting the firm
          does not create an attorney-client relationship. An attorney-client relationship is
          established only through a written agreement signed by the client and the firm. Past
          results do not guarantee future outcomes. Each estate plan depends on its specific
          facts and applicable law.
          <div className="max-w-3xl">
            <AttorneyReviewNote reviewed="February 2026" variant="dark" compact />
          </div>
        </div>
      </section>
    </div>
  );
}
